import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check } from 'lucide-react';
import { Link } from '@/i18n/navigation';
import becomeTutorImage from '@/assets/become_tutor/become_tutor.png';
import { useModal } from '@/contexts';
import { useI18n } from '@/i18n';

const tutorBenefits = [
  'Find new students',
  'Secure your payments',
  'Grow your own business',
];

interface BecomeTutorSectionProps {
  variant?: 'academic' | 'music';
}

const BecomeTutorSection: React.FC<BecomeTutorSectionProps> = ({ variant = 'academic' }) => {
  const { openMusicTutorRegistration } = useModal();
  const { locale } = useI18n();
  const isMusic = variant === 'music';
  const isZh = locale === 'zh-CN';
  const academicBenefits = isZh
    ? ['接触更多新学生', '保障收款流程', '建立属于你的补习事业']
    : tutorBenefits;

  return (
    <section className={`${isMusic ? 'bg-primary-100' : 'bg-[#F6D15B]'} py-14 md:bg-white md:py-20`}>
      <div className="container mx-auto max-w-6xl px-4">
        <div className={`bg-transparent px-0 py-8 md:rounded-[2.5rem] md:px-10 md:py-12 md:shadow-xl lg:px-12 ${isMusic ? 'md:bg-primary-100' : 'md:bg-[#F6D15B]'}`}>
          <div className="grid items-center gap-8 lg:grid-cols-2 lg:gap-14">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.55 }}
            viewport={{ once: true, amount: 0.3 }}
            className="order-1 lg:order-1"
          >
            <div className="overflow-hidden rounded-[2rem] shadow-[0_22px_60px_rgba(217,137,61,0.18)]">
              <img
                src={becomeTutorImage}
                alt="Tutor Founder benefits placeholder"
                className="aspect-[4/3] w-full object-cover"
              />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.55 }}
            viewport={{ once: true, amount: 0.3 }}
            className="order-2 flex justify-center lg:order-2"
          >
            <div className="w-full max-w-md text-left">
              <h2 className="mt-3 text-4xl font-bold leading-tight tracking-tight text-black md:text-5xl">
                {isMusic ? 'Become a music tutor' : isZh ? '成为导师' : 'Become a tutor'}
              </h2>

              <div className="mt-6 space-y-4">
                {(isMusic ? tutorBenefits : academicBenefits).map((benefit) => (
                  <div key={benefit} className="flex items-start gap-3">
                    <div className={`mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${isMusic ? 'bg-primary-50 text-primary' : 'bg-[#F8E2A0] text-[#946C00]'}`}>
                      <Check className="h-4 w-4" strokeWidth={3} />
                    </div>
                    <p className="text-base font-semibold leading-7 text-black/85 md:text-lg">{benefit}</p>
                  </div>
                ))}
              </div>

              <div className="mt-8">
                {isMusic ? (
                  <button
                    type="button"
                    onClick={openMusicTutorRegistration}
                    className="inline-flex cursor-pointer items-center gap-2 rounded-2xl bg-primary px-7 py-3.5 text-base font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-primary-800 hover:shadow-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300"
                  >
                    <span>Become a music tutor</span>
                    <ArrowRight className="h-4 w-4 shrink-0" />
                  </button>
                ) : (
                  <Link
                    to="/teach"
                    className="inline-flex items-center gap-2 rounded-2xl bg-black px-7 py-3.5 text-base font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#1F1F1F] hover:shadow-[0_18px_36px_rgba(0,0,0,0.2)] focus:outline-none focus-visible:ring-2 focus-visible:ring-black/20"
                  >
                    <span>{isZh ? '成为导师' : 'Become a tutor'}</span>
                    <ArrowRight className="h-4 w-4 shrink-0" />
                  </Link>
                )}
              </div>

              <div className="mt-5">
                {isMusic ? (
                  <button type="button" onClick={openMusicTutorRegistration} className="cursor-pointer text-sm font-semibold text-gray-800 underline decoration-gray-500 underline-offset-4 transition-colors duration-200 hover:text-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-900">
                    Start your Music profile
                  </button>
                ) : (
                  <Link
                    to="/how-it-works?tab=tutor"
                    className="text-sm font-semibold text-gray-800 underline decoration-gray-500 underline-offset-4 transition-colors duration-200 hover:text-black"
                  >
                    {isZh ? '查看运作方式' : 'How it works ?'}
                  </Link>
                )}
              </div>
            </div>
          </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BecomeTutorSection;
